import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAppTranslations } from '@/hooks/useAppTranslations';

// Premium Color Palette from PREMIUM_METRIC_PATTERNS.md
const PREMIUM_COLORS = {
  primaryGolfGreen: '#1B4332',
  secondaryGolfGreen: '#2D5A3D',
  backgroundMint: '#F0FDF4',
  accentBrightGreen: '#52B788',
  neutralGray: '#9CA3AF',
  darkText: '#374151'
};

export interface VirtualScrollItem {
  id: string | number;
  height?: number; // Optional: custom height for this item
  data: any; // The actual data for the item
}

export interface VirtualScrollListProps<T extends VirtualScrollItem> {
  items: T[];
  itemHeight?: number; // Default height for items
  containerHeight: number; // Height of the scroll container
  overscan?: number; // Number of items to render outside visible area
  renderItem: (item: T, index: number) => React.ReactNode;
  className?: string;
  onScrollEnd?: () => void; // Callback when user reaches end of list
  loading?: boolean;
  error?: string | null;
  emptyMessage?: string;
}

/**
 * High-Performance Virtual Scrolling Component
 * 
 * Optimized for large datasets in Performance Zones:
 * - Only renders visible items + overscan buffer
 * - Smooth scrolling with momentum
 * - iOS-optimized touch handling
 * - Memory efficient for 1000+ items
 * - Automatic height calculation
 * - Pull-to-refresh compatible
 */
export function VirtualScrollList<T extends VirtualScrollItem>({
  items,
  itemHeight = 80,
  containerHeight,
  overscan = 5,
  renderItem,
  className = '',
  onScrollEnd,
  loading = false,
  error = null,
  emptyMessage
}: VirtualScrollListProps<T>) {
  const { t } = useAppTranslations();
  const [scrollTop, setScrollTop] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const scrollElementRef = useRef<HTMLDivElement>(null);
  const lastScrollTime = useRef(Date.now());
  const scrollEndTimeoutRef = useRef<NodeJS.Timeout>();

  // Calculate item heights - support variable heights
  const itemHeights = useMemo(() => {
    return items.map(item => item.height || itemHeight);
  }, [items, itemHeight]);

  // Calculate total height
  const totalHeight = useMemo(() => {
    return itemHeights.reduce((sum, height) => sum + height, 0);
  }, [itemHeights]);

  // Calculate which items are visible
  const visibleRange = useMemo(() => {
    if (items.length === 0) return { start: 0, end: 0 };

    let accumulatedHeight = 0;
    let startIndex = 0;
    let endIndex = 0;

    // Find start index
    for (let i = 0; i < items.length; i++) {
      if (accumulatedHeight + itemHeights[i] > scrollTop) {
        startIndex = Math.max(0, i - overscan);
        break;
      }
      accumulatedHeight += itemHeights[i];
    }

    // Find end index
    accumulatedHeight = 0;
    for (let i = 0; i < items.length; i++) {
      accumulatedHeight += itemHeights[i];
      if (accumulatedHeight > scrollTop + containerHeight) {
        endIndex = Math.min(items.length - 1, i + overscan);
        break;
      }
    }

    // If we didn't find an end, show all remaining items
    if (endIndex === 0) {
      endIndex = items.length - 1;
    }

    return { start: startIndex, end: endIndex };
  }, [scrollTop, containerHeight, items.length, itemHeights, overscan]);

  // Calculate offset for visible items
  const offsetY = useMemo(() => {
    let offset = 0;
    for (let i = 0; i < visibleRange.start; i++) {
      offset += itemHeights[i];
    }
    return offset;
  }, [visibleRange.start, itemHeights]);

  // Get visible items to render
  const visibleItems = useMemo(() => {
    return items.slice(visibleRange.start, visibleRange.end + 1);
  }, [items, visibleRange.start, visibleRange.end]);

  // Handle scroll with throttling for performance
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const currentTime = Date.now();
    
    // Throttle scroll events to ~60fps
    if (currentTime - lastScrollTime.current < 16) {
      return;
    }
    
    lastScrollTime.current = currentTime;
    const newScrollTop = e.currentTarget.scrollTop;
    setScrollTop(newScrollTop);

    // Handle scroll end detection
    if (onScrollEnd) {
      if (scrollEndTimeoutRef.current) {
        clearTimeout(scrollEndTimeoutRef.current);
      }
      
      scrollEndTimeoutRef.current = setTimeout(() => {
        const element = e.currentTarget;
        const isAtEnd = element.scrollTop + element.clientHeight >= element.scrollHeight - 10;
        
        if (isAtEnd) {
          onScrollEnd();
        }
      }, 150);
    }
  }, [onScrollEnd]);

  // Handle container resize
  useEffect(() => {
    const handleResize = () => {
      if (scrollElementRef.current) {
        setContainerWidth(scrollElementRef.current.clientWidth);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (scrollEndTimeoutRef.current) {
        clearTimeout(scrollEndTimeoutRef.current);
      }
    };
  }, []);

  // Handle loading state
  if (loading) {
    return (
      <div 
        className={`flex items-center justify-center ${className}`}
        style={{ height: containerHeight }}
      >
        <div className="text-center">
          <div 
            className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto mb-4"
            style={{ borderColor: PREMIUM_COLORS.accentBrightGreen }}
          />
          <p className="text-sm" style={{ color: PREMIUM_COLORS.secondaryGolfGreen }}>
            {t.performanceZones.loading}
          </p>
        </div>
      </div>
    );
  }

  // Handle error state
  if (error) {
    return (
      <div 
        className={`flex items-center justify-center ${className}`}
        style={{ height: containerHeight }}
      >
        <div className="text-center p-4">
          <div 
            className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4"
            style={{ backgroundColor: 'rgba(220, 38, 38, 0.1)' }}
          >
            <span className="text-red-500 text-xl">⚠</span>
          </div>
          <p className="text-sm" style={{ color: PREMIUM_COLORS.primaryGolfGreen }}>
            {error}
          </p>
        </div>
      </div>
    );
  }

  // Handle empty state
  if (items.length === 0) {
    return (
      <div 
        className={`flex items-center justify-center ${className}`}
        style={{ height: containerHeight }}
      >
        <div className="text-center p-4">
          <div 
            className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4"
            style={{ backgroundColor: 'rgba(156, 163, 175, 0.1)' }}
          >
            <span className="text-gray-400 text-xl">📋</span>
          </div>
          <p className="text-sm" style={{ color: PREMIUM_COLORS.secondaryGolfGreen }}>
            {emptyMessage || t.performanceZones.noItemsToDisplay}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={scrollElementRef}
      className={`overflow-auto ${className}`}
      style={{ 
        height: containerHeight,
        scrollBehavior: 'smooth',
        WebkitOverflowScrolling: 'touch' // iOS momentum scrolling
      }}
      onScroll={handleScroll}
    >
      {/* Virtual container with total height */}
      <div style={{ height: totalHeight, position: 'relative' }}>
        {/* Visible items container */}
        <div
          style={{
            transform: `translateY(${offsetY}px)`,
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0
          }}
        >
          {visibleItems.map((item, index) => (
            <div key={item.id} style={{ height: itemHeights[visibleRange.start + index] }}>
              {renderItem(item, visibleRange.start + index)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Hook for virtual scrolling with performance optimizations
 */
export function useVirtualScroll<T extends VirtualScrollItem>(items: T[], itemHeight = 80) {
  const [visibleItems, setVisibleItems] = useState<T[]>([]);
  const [hasMore, setHasMore] = useState(true);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50; // Load 50 items at a time

  // Get current page items
  const pagedItems = useMemo(() => {
    const startIndex = 0;
    const endIndex = currentPage * itemsPerPage;
    return items.slice(startIndex, endIndex);
  }, [items, currentPage, itemsPerPage]);

  // Load more items when scrolling to end
  const loadMore = useCallback(() => {
    if (currentPage * itemsPerPage < items.length) {
      setCurrentPage(prev => prev + 1);
    } else {
      setHasMore(false);
    }
  }, [currentPage, itemsPerPage, items.length]);

  // Reset when items change
  useEffect(() => {
    setCurrentPage(1);
    setHasMore(items.length > itemsPerPage);
  }, [items, itemsPerPage]);

  return {
    pagedItems,
    loadMore,
    hasMore,
    isAtEnd: currentPage * itemsPerPage >= items.length
  };
}